import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AgentThread, GetAgentThreadsResponse } from '@dreamai/types';
import { useAgent } from '../../../contexts/AgentContext';
import { useThreadReads } from '../../../providers/ThreadReadsProvider';
import api from '../../../api';
import { ReactComponent as PlusSquareIcon } from '../../../icons/plus-square.svg';
import { ReactComponent as SendIcon } from '../../../icons/send.svg';
import { ReactComponent as CampaignIcon } from '../../../icons/campaign.svg';
import { ReactComponent as ClipboardIcon } from '../../../icons/clipboard-list.svg';
import { ReactComponent as MentionAtIcon } from '../../../icons/mention-at.svg';
import NewThreadModal from './NewThreadModal';
import './side-nav-section.scss';

interface SideNavSectionProps {
  agentId: string;
}

export interface SideNavSectionRef {
  refreshThreads: () => void;
}

const SideNavSection = forwardRef<SideNavSectionRef, SideNavSectionProps>(
  ({ agentId }, ref) => {
    const { getAccessTokenSilently } = useAuth0();
    const { getAgentById } = useAgent();
    const [searchParams] = useSearchParams();
    const { getUnreadThreadIdsByAgent } = useThreadReads();

    // Get current thread_id from URL
    const currentThreadId = searchParams.get('thread_id');

    // Get agent details from context
    const agent = getAgentById(agentId);

    // Get unread thread IDs from the provider (source of truth)
    const unreadThreadIds = useMemo(
      () => new Set(getUnreadThreadIdsByAgent(agentId)),
      [getUnreadThreadIdsByAgent, agentId],
    );

    // Local state for threads
    const [threads, setThreads] = useState<AgentThread[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isNewThreadModalOpen, setIsNewThreadModalOpen] = useState(false);

    // Fetch threads for this agent
    const fetchThreads = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();
        const response = await api.get<GetAgentThreadsResponse>(
          '/agents/threads',
          {
            params: {
              agent_id: agentId,
              excludePinned: 'true',
              excludeShared: 'true',
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data.success) {
          setThreads(response.data.threads);
        } else {
          setError('Failed to load threads');
        }
      } catch (err) {
        console.error(`Error fetching threads for agent ${agentId}:`, err);
        setError(err instanceof Error ? err.message : 'Failed to load threads');
      } finally {
        setLoading(false);
      }
    };

    // Expose refreshThreads to parent via ref
    useImperativeHandle(ref, () => ({
      refreshThreads: fetchThreads,
    }));

    useEffect(() => {
      fetchThreads();
    }, [agentId, getAccessTokenSilently]);

    // State for expanded view
    const [isExpanded, setIsExpanded] = useState(false);
    const MAX_VISIBLE_THREADS = 5;

    // Helper to check if thread has ExpectedCall discussing object
    const hasExpectedCallDiscussingObject = (thread: AgentThread) =>
      thread.discussing_objects?.some(
        (obj) => obj.discussing_object === 'ExpectedCall',
      );

    // Filter out background/internal/learning/archived threads (same as ConversationList)
    const visibleThreads = useMemo(
      () =>
        threads.filter(
          (t) =>
            !t.is_archived_thread &&
            !t.is_background &&
            !t.is_internal_monologue &&
            !t.is_learning_thread &&
            !hasExpectedCallDiscussingObject(t),
        ),
      [threads],
    );

    // Get hotline thread (always shown first)
    const hotlineThread = visibleThreads.find(
      (t) => t.is_main_thread && t.include_in_headings,
    );

    // Get all non-hotline threads and sort by priority
    const otherThreads = useMemo(() => {
      const nonHotline = visibleThreads.filter(
        (t) => !t.is_main_thread || !t.include_in_headings,
      );

      // Sort by priority: mentions > confirmations > unread > others
      return nonHotline.sort((a, b) => {
        // Priority 1: Has unread mentions
        if (a.has_unread_mentions && !b.has_unread_mentions) return -1;
        if (!a.has_unread_mentions && b.has_unread_mentions) return 1;

        // Priority 2: Has pending confirmations
        if (a.has_pending_confirmations && !b.has_pending_confirmations)
          return -1;
        if (!a.has_pending_confirmations && b.has_pending_confirmations)
          return 1;

        // Priority 3: Is unread
        const aUnread = unreadThreadIds.has(a.id);
        const bUnread = unreadThreadIds.has(b.id);
        if (aUnread && !bUnread) return -1;
        if (!aUnread && bUnread) return 1;

        // Default: maintain original order
        return 0;
      });
    }, [visibleThreads, unreadThreadIds]);

    // Helper to check if thread should be visible (has priority or recent activity)
    const isVisibleThread = useMemo(() => {
      const fiveDaysAgo = new Date();
      fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);

      return (t: AgentThread) => {
        // Priority threads are always visible
        if (
          t.has_unread_mentions ||
          t.has_pending_confirmations ||
          unreadThreadIds.has(t.id)
        ) {
          return true;
        }
        // Others only if active in past 5 days
        const lastUpdated = new Date(t.last_updated_at);
        return lastUpdated >= fiveDaysAgo;
      };
    }, [unreadThreadIds]);

    // Calculate how many threads to show (hotline counts as 1 if present)
    const hotlineCount = hotlineThread ? 1 : 0;
    const maxOtherThreads = MAX_VISIBLE_THREADS - hotlineCount;

    // For displayed threads: filter by visibility, then limit to max
    // Always show at least one thread if there are any, even if none pass the filter
    const eligibleThreads = otherThreads.filter(isVisibleThread);
    const displayedThreads = isExpanded
      ? otherThreads
      : eligibleThreads.length > 0
        ? eligibleThreads.slice(0, maxOtherThreads)
        : otherThreads.slice(0, Math.max(1, maxOtherThreads));

    // Hidden threads: everything not in displayedThreads
    const displayedIds = new Set(displayedThreads.map((t) => t.id));
    const hiddenThreads = otherThreads.filter((t) => !displayedIds.has(t.id));
    const hasHiddenThreads = !isExpanded && hiddenThreads.length > 0;

    // Calculate hidden thread stats for the expand button
    const hiddenStats = useMemo(() => {
      if (!hasHiddenThreads) return null;

      const hiddenCount = hiddenThreads.length;
      const hiddenMentions = hiddenThreads.filter(
        (t) => t.has_unread_mentions,
      ).length;
      const hiddenActions = hiddenThreads.filter(
        (t) => t.has_pending_confirmations,
      ).length;
      const hiddenUnread = hiddenThreads.filter((t) =>
        unreadThreadIds.has(t.id),
      ).length;

      return { hiddenCount, hiddenMentions, hiddenActions, hiddenUnread };
    }, [hasHiddenThreads, hiddenThreads, unreadThreadIds]);

    const getThreadIcon = (thread: AgentThread) => {
      let icon = <SendIcon />;
      if (
        thread.discussing_objects?.some(
          (d) => d.discussing_object === 'Campaign',
        )
      ) {
        icon = <CampaignIcon />;
      } else if (
        thread.discussing_objects?.some((d) => d.discussing_object === 'Plan')
      ) {
        icon = <ClipboardIcon />;
      }

      // Unread mentions: show the @ icon instead
      if (thread.has_unread_mentions) {
        return <MentionAtIcon />;
      }

      // Pending confirmations: show action-required indicator
      if (thread.has_pending_confirmations) {
        return (
          <div className='thread-icon-wrapper'>
            {icon}
            <div className='action-required-indicator'>
              <div className='action-required-indicator-dot' />
            </div>
          </div>
        );
      }

      return icon;
    };

    const getThreadName = (thread: AgentThread) => {
      if (thread.is_main_thread) return 'Direct Message';
      return thread.description || 'Untitled';
    };

    const getThreadClasses = (thread: AgentThread) => {
      const classes = ['thread-item'];
      if (currentThreadId === thread.id) classes.push('active');
      // Use unreadThreadIds from provider as source of truth
      if (unreadThreadIds.has(thread.id)) classes.push('has-unread');
      if (thread.has_pending_confirmations)
        classes.push('has-pending-confirmations');
      if (thread.has_unread_mentions) classes.push('has-unread-mentions');
      return classes.join(' ');
    };

    return (
      <div className='side-nav-section' data-agent-id={agentId}>
        <div className='section-header'>
          <div className='agent-info'>
            <div className='dreamie-image-wrapper'>
              {agent?.image_url && (
                <img src={agent.image_url} alt={agent.nickname} />
              )}
            </div>
            <p className='nickname'>{agent?.nickname}</p>
          </div>
          <button
            className='add-thread-btn'
            onClick={() => setIsNewThreadModalOpen(true)}
          >
            <PlusSquareIcon />
          </button>
        </div>

        <div className='threads'>
          {/* Hotline / Direct Message - always first */}
          {hotlineThread && (
            <Link
              to={`/conversations?agent_id=${agentId}&thread_id=${hotlineThread.id}`}
              className={getThreadClasses(hotlineThread)}
            >
              {getThreadIcon(hotlineThread)}
              <span>{getThreadName(hotlineThread)}</span>
            </Link>
          )}

          {/* Other threads sorted by priority */}
          {displayedThreads.map((thread) => (
            <Link
              key={thread.id}
              to={`/conversations?agent_id=${agentId}&thread_id=${thread.id}`}
              className={getThreadClasses(thread)}
            >
              {getThreadIcon(thread)}
              <span>{getThreadName(thread)}</span>
            </Link>
          ))}

          {/* Expand button for hidden threads */}
          {hasHiddenThreads && hiddenStats && (
            <button
              className={`expand-threads-btn${hiddenStats.hiddenMentions > 0 ? ' mention' : ''}${hiddenStats.hiddenActions > 0 ? ' action' : ''}${hiddenStats.hiddenUnread > 0 ? ' unread' : ''}`}
              onClick={() => setIsExpanded(true)}
            >
              + {hiddenStats.hiddenCount} more chats
              {(hiddenStats.hiddenMentions > 0 ||
                hiddenStats.hiddenActions > 0 ||
                hiddenStats.hiddenUnread > 0) && (
                <span className='hidden-stats'>
                  (
                  {[
                    hiddenStats.hiddenMentions > 0 &&
                      `${hiddenStats.hiddenMentions} mentions`,
                    hiddenStats.hiddenActions > 0 &&
                      `${hiddenStats.hiddenActions} actions`,
                    hiddenStats.hiddenUnread > 0 &&
                      `${hiddenStats.hiddenUnread} unread`,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                  )
                </span>
              )}
            </button>
          )}

          {/* Collapse button when expanded */}
          {isExpanded && otherThreads.length > maxOtherThreads && (
            <button
              className='expand-threads-btn'
              onClick={() => setIsExpanded(false)}
            >
              Show less
            </button>
          )}
        </div>

        <NewThreadModal
          isOpen={isNewThreadModalOpen}
          onClose={() => setIsNewThreadModalOpen(false)}
          agentId={agentId}
          agentName={agent?.nickname}
          agentImageUrl={agent?.image_url}
          agentType={agent?.agent_type}
          onThreadCreated={fetchThreads}
        />
      </div>
    );
  },
);

SideNavSection.displayName = 'SideNavSection';

export default SideNavSection;
